/**
 * LinkOSx Brand Configuration
 * Legal Staffing Platform - Connecting trained Chinese legal professionals with overseas law firms
 *
 * brandIdentifier matches tenant.name in the backend ("linkosx")
 * Backend email service resolves branding from tenant config directly.
 */

export interface BrandConfig {
  siteName: string;
  tagline: string;
  description: string;
  tenantId: string;
  apiUrl?: string;
  brandIdentifier: string;
  logo: {
    src: string;
    alt: string;
    favicon: string;
  };
  contact: {
    businessType: string;
    industry: string;
    email?: string;
    phone?: string;
    address?: string;
    contactName?: string;
    businessName?: string;
  };
  colors: {
    accentBlue?: string;
    primaryBlue?: string;
    primaryBlueHover?: string;
    starYellow?: string;
    starYellowHover?: string;
    successGreen?: string;
    purpleAccent?: string;
    taskCardBg?: string;
    emailPreviewBg?: string;
    darkCardBg?: string;
    darkBorderSubtle?: string;
    highlightYellow?: string;
    highlightGreen?: string;
    highlightBlue?: string;
    highlightPurple?: string;
    highlightRed?: string;
    badges?: any;
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;
    accent: string;
    accentLight: string;
    background: {
      main: string;
      light: string;
      card: string;
      overlay: string;
      surface: string;
      darkSurface?: string;
      cardGradient?: string;
      darkOverlay?: string;
      taskCard?: string;
      previewBg?: string;
    };
    status: {
      success: string;
      warning: string;
      error: string;
      info: string;
      draft?: string;
      sent?: string;
      failed?: string;
      scheduled?: string;
      inbound?: string;
    };
    text: {
      primary: string;
      secondary: string;
      muted: string;
      inverse: string;
      primaryDark?: string;
      secondaryDark?: string;
      mutedDark?: string;
    };
    border: {
      light: string;
      medium: string;
      dark: string;
      darkCard?: string;
    };
  };
  fonts: {
    heading: string;
    body: string;
  };
  components: {
    button: {
      primaryBg: string;
      primaryHover: string;
      secondaryBg: string;
      secondaryHover: string;
      whiteBg?: string;
      whiteText?: string;
      whiteHover?: string;
      blueBg?: string;
      blueHover?: string;
    };
    tooltip?: {
      bg: string;
      color: string;
      errorBg?: string;
      darkBg?: string;
    };
    menu?: {
      bg: string;
      itemBg: string;
      itemColor: string;
      itemHoverBg: string;
      itemHoverColor: string;
      borderColor: string;
    };
    card: {
      bg: string;
      border: string;
      shadow: string;
      darkShadow?: string;
      backdropFilter?: string;
      hoverShadow?: string;
    };
    navbar: {
      bg: string;
      text: string;
      shadow: string;
    };
    footer: {
      bg: string;
      text: string;
      linkColor: string;
      linkHover: string;
    };
    form: {
      bg: string;
      fieldBg: string;
      fieldBorder: string;
      fieldBorderFocus: string;
      fieldShadow: string;
      fieldShadowFocus: string;
      labelColor: string;
      placeholderColor: string;
    };
  };
  membership: {
    totalLegacyMemberships: number;
    memberBenefits: {
      discount: string;
      features: string[];
    };
  };
}

// LinkOSx Configuration - Navy Blue Professional Legal Theme
export const brandConfig: BrandConfig = {
  siteName: "LinkOSx",
  tagline: "Legal Staffing. Simplified.",
  description: "Connecting trained Chinese legal professionals with overseas law firms. Comprehensive staffing platform with worker profiles, certifications, and time tracking.",

  // Tenant ID - will be updated after tenant registration
  tenantId: "69ae80e7eceee3416686c7c7",
  brandIdentifier: "linkosx",

  logo: {
    src: "LinkOSx",
    alt: "LinkOSx Logo",
    favicon: "%PUBLIC_URL%/favicon.ico"
  },

  contact: {
    businessType: "Legal Staffing Platform",
    industry: "Legal Services",
    email: "info@linkosx.com",
    contactName: "Joanna Chen",
    businessName: "LinkOSx"
  },

  // Navy Blue Professional Color Palette
  colors: {
    primary: "#1E3A5F",
    primaryHover: "#152C49",
    secondary: "#4A90D9",
    secondaryHover: "#3A7BC8",

    accent: "#2563EB",
    accentLight: "#BFDBFE",

    background: {
      main: "#0B1929",
      light: "#F8FAFC",
      card: "#FFFFFF",
      overlay: "rgba(30, 58, 95, 0.05)",
      surface: "#F1F5F9",
      darkSurface: "#0B1929",
      cardGradient: "linear-gradient(135deg, rgba(11, 25, 41, 0.8) 0%, rgba(30, 58, 95, 0.6) 50%, rgba(11, 25, 41, 0.8) 100%)",
      darkOverlay: "rgba(0, 0, 0, 0.2)",
      taskCard: "rgba(30, 58, 95, 0.3)",
      previewBg: "rgba(30, 58, 95, 0.3)"
    },

    status: {
      success: "#22C55E",
      warning: "#F59E0B",
      error: "#EF4444",
      info: "#2563EB",
      draft: "#6B7280",
      sent: "#22C55E",
      failed: "#EF4444",
      scheduled: "#2563EB",
      inbound: "#8B5CF6"
    },

    text: {
      primary: "#0F172A",
      secondary: "#475569",
      muted: "#94A3B8",
      inverse: "#FFFFFF",
      primaryDark: "#FFFFFF",
      secondaryDark: "rgba(255, 255, 255, 0.7)",
      mutedDark: "rgba(255, 255, 255, 0.5)"
    },

    border: {
      light: "rgba(0, 0, 0, 0.06)",
      medium: "rgba(0, 0, 0, 0.1)",
      dark: "rgba(0, 0, 0, 0.2)",
      darkCard: "rgba(255, 255, 255, 0.1)"
    },

    accentBlue: "#2563EB",
    primaryBlue: "#1E3A5F",
    primaryBlueHover: "#152C49",
    starYellow: "#FBBF24",
    starYellowHover: "#F59E0B",
    successGreen: "#22C55E",
    purpleAccent: "#8B5CF6",

    taskCardBg: "rgba(30, 58, 95, 0.3)",
    emailPreviewBg: "rgba(30, 58, 95, 0.3)",
    darkCardBg: "rgba(11, 25, 41, 0.9)",
    darkBorderSubtle: "rgba(255, 255, 255, 0.05)",
    highlightYellow: "#FEF3C7",
    highlightGreen: "#D1FAE5",
    highlightBlue: "#DBEAFE",
    highlightPurple: "#E9D5FF",
    highlightRed: "#FEE2E2",

    badges: {
      blue: {
        bg: "rgba(37, 99, 235, 0.2)",
        color: "#2563EB",
        border: "rgba(37, 99, 235, 0.3)"
      },
      green: {
        bg: "rgba(34, 197, 94, 0.2)",
        color: "#22C55E",
        border: "rgba(34, 197, 94, 0.3)"
      },
      purple: {
        bg: "rgba(139, 92, 246, 0.2)",
        color: "#8B5CF6",
        border: "rgba(139, 92, 246, 0.3)"
      },
      yellow: {
        bg: "rgba(251, 191, 36, 0.2)",
        color: "#FBBF24",
        border: "rgba(251, 191, 36, 0.3)"
      },
      gray: {
        bg: "rgba(107, 114, 128, 0.2)",
        color: "#6B7280",
        border: "rgba(107, 114, 128, 0.3)"
      },
      orange: {
        bg: "rgba(251, 146, 60, 0.2)",
        color: "#FB923C",
        border: "rgba(251, 146, 60, 0.3)"
      },
      teal: {
        bg: "rgba(20, 184, 166, 0.2)",
        color: "#14B8A6",
        border: "rgba(20, 184, 166, 0.3)"
      },
      cyan: {
        bg: "rgba(6, 182, 212, 0.2)",
        color: "#06B6D4",
        border: "rgba(6, 182, 212, 0.3)"
      }
    }
  },

  // System fonts only - China-friendly, NO Google Fonts
  fonts: {
    heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', system-ui, sans-serif",
    body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', system-ui, sans-serif"
  },

  components: {
    button: {
      primaryBg: "#1E3A5F",
      primaryHover: "#152C49",
      secondaryBg: "rgba(30, 58, 95, 0.1)",
      secondaryHover: "rgba(30, 58, 95, 0.2)",
      whiteBg: "#FFFFFF",
      whiteText: "#0F172A",
      whiteHover: "#F1F5F9",
      blueBg: "#2563EB",
      blueHover: "#1D4ED8"
    },
    tooltip: {
      bg: "#1E3A5F",
      color: "#FFFFFF",
      errorBg: "#EF4444",
      darkBg: "#0B1929"
    },
    menu: {
      bg: "rgba(11, 25, 41, 0.98)",
      itemBg: "transparent",
      itemColor: "#E2E8F0",
      itemHoverBg: "rgba(74, 144, 217, 0.2)",
      itemHoverColor: "#FFFFFF",
      borderColor: "rgba(74, 144, 217, 0.3)"
    },
    card: {
      bg: "#FFFFFF",
      border: "rgba(0, 0, 0, 0.06)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#0B1929",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#0B1929",
      text: "rgba(255, 255, 255, 0.7)",
      linkColor: "#4A90D9",
      linkHover: "#2563EB"
    },
    form: {
      bg: "#FFFFFF",
      fieldBg: "#FFFFFF",
      fieldBorder: "rgba(0, 0, 0, 0.1)",
      fieldBorderFocus: "#1E3A5F",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
      fieldShadowFocus: "0 0 0 3px rgba(30, 58, 95, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05)",
      labelColor: "#0F172A",
      placeholderColor: "#94A3B8"
    }
  },

  membership: {
    totalLegacyMemberships: 100,
    memberBenefits: {
      discount: "Professional rates",
      features: [
        "Worker Profiles & CVs",
        "Legal Software Certifications",
        "Time Tracking",
        "Client Management"
      ]
    }
  },
};

// Theme-specific color configurations
export const lightTheme = {
  primary: "#1E3A5F",
  primaryHover: "#152C49",
  secondary: "#4A90D9",
  secondaryHover: "#3A7BC8",

  accent: "#2563EB",
  accentLight: "#BFDBFE",

  background: {
    main: "#FFFFFF",
    light: "#F8FAFC",
    card: "#FFFFFF",
    overlay: "rgba(0, 0, 0, 0.05)",
    surface: "#F1F5F9",
    cardGradient: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
    darkOverlay: "rgba(0, 0, 0, 0.1)",
    taskCard: "#F8FAFC",
    previewBg: "#F8FAFC"
  },

  text: {
    primary: "#0F172A",
    secondary: "#475569",
    muted: "#94A3B8",
    inverse: "#FFFFFF",
    primaryDark: "#0F172A",
    secondaryDark: "#475569",
    mutedDark: "#94A3B8"
  },

  border: {
    light: "#E2E8F0",
    medium: "#CBD5E0",
    dark: "#94A3B8",
    darkCard: "#E2E8F0"
  },

  components: {
    card: {
      bg: "#FFFFFF",
      border: "#E2E8F0",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
      darkShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
      backdropFilter: "none"
    },
    navbar: {
      bg: "#FFFFFF",
      text: "#0F172A",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#F8FAFC",
      text: "#475569",
      linkColor: "#1E3A5F",
      linkHover: "#152C49"
    },
    button: {
      primaryBg: "#1E3A5F",
      primaryHover: "#152C49",
      secondaryBg: "rgba(30, 58, 95, 0.1)",
      secondaryHover: "rgba(30, 58, 95, 0.2)",
      whiteBg: "#0F172A",
      whiteText: "#FFFFFF",
      whiteHover: "#1E293B",
      blueBg: "#2563EB",
      blueHover: "#1D4ED8"
    }
  }
};

export const darkTheme = {
  primary: "#1E3A5F",
  primaryHover: "#152C49",
  secondary: "#4A90D9",
  secondaryHover: "#3A7BC8",

  accent: "#2563EB",
  accentLight: "#BFDBFE",

  background: {
    main: "#0B1929",
    light: "#F8FAFC",
    card: "#FFFFFF",
    overlay: "rgba(30, 58, 95, 0.05)",
    surface: "#F1F5F9",
    darkSurface: "#0B1929",
    cardGradient: "linear-gradient(135deg, rgba(11, 25, 41, 0.8) 0%, rgba(30, 58, 95, 0.6) 50%, rgba(11, 25, 41, 0.8) 100%)",
    darkOverlay: "rgba(0, 0, 0, 0.2)",
    taskCard: "rgba(30, 58, 95, 0.3)",
    previewBg: "rgba(30, 58, 95, 0.3)"
  },

  text: {
    primary: "#FFFFFF",
    secondary: "rgba(255, 255, 255, 0.7)",
    muted: "rgba(255, 255, 255, 0.5)",
    inverse: "#0F172A",
    primaryDark: "#FFFFFF",
    secondaryDark: "rgba(255, 255, 255, 0.7)",
    mutedDark: "rgba(255, 255, 255, 0.5)"
  },

  border: {
    light: "rgba(0, 0, 0, 0.05)",
    medium: "rgba(0, 0, 0, 0.1)",
    dark: "rgba(0, 0, 0, 0.2)",
    darkCard: "rgba(255, 255, 255, 0.1)"
  },

  components: {
    card: {
      bg: "#FFFFFF",
      border: "rgba(0, 0, 0, 0.06)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#0B1929",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#0B1929",
      text: "rgba(255, 255, 255, 0.7)",
      linkColor: "#4A90D9",
      linkHover: "#2563EB"
    },
    button: {
      primaryBg: "#1E3A5F",
      primaryHover: "#152C49",
      secondaryBg: "rgba(30, 58, 95, 0.1)",
      secondaryHover: "rgba(30, 58, 95, 0.2)",
      whiteBg: "#FFFFFF",
      whiteText: "#0F172A",
      whiteHover: "#F1F5F9",
      blueBg: "#2563EB",
      blueHover: "#1D4ED8"
    },
    form: {
      bg: "rgba(11, 25, 41, 0.8)",
      fieldBg: "rgba(30, 58, 95, 0.8)",
      fieldBorder: "rgba(255, 255, 255, 0.1)",
      fieldBorderFocus: "#4A90D9",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(74, 144, 217, 0.2), 0 1px 2px rgba(0, 0, 0, 0.3)",
      labelColor: "#FFFFFF"
    }
  }
};

// Helper function with color mode support
export const getColor = (path: string, colorMode?: 'light' | 'dark'): string => {
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const keys = path.split(".");

  if (keys[0] === "components") {
    let value: any = theme;
    for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        let fallback: any = brandConfig.colors;
        const colorKeys = keys.slice(1);
        for (const key of colorKeys) {
          fallback = fallback[key];
          if (fallback === undefined) break;
        }
        if (fallback !== undefined) return fallback;
        console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
        return "#1E3A5F";
      }
    }
    return value;
  }

  let value: any = theme;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      let fallback: any = brandConfig.colors;
      for (const key of keys) {
        fallback = fallback[key];
        if (fallback === undefined) break;
      }
      if (fallback !== undefined) return fallback;
      console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
      return "#1E3A5F";
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }

  return value;
};

export const getComponent = (component: string, property: string, colorMode?: 'light' | 'dark'): string => {
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const componentConfig = (theme.components as any)?.[component];
  if (!componentConfig) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const value = fallbackConfig[property];
      if (value) return value;
    }
    console.warn(`Component "${component}" not found in theme config for mode: ${mode}`);
    return "#1E3A5F";
  }

  const value = componentConfig[property];
  if (!value) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const fallbackValue = fallbackConfig[property];
      if (fallbackValue) return fallbackValue;
    }
    console.warn(`Property "${property}" not found for component "${component}" in mode: ${mode}`);
    return "#1E3A5F";
  }

  return value;
};

// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;

  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};

      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};

      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};

      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};

      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};

      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

export default brandConfig;
